import { useEffect, useState } from 'react';
import { classifyLogo, type LogoTone } from '../components/logo-tone.js';

/**
 * Как показать логотип канала крупно на тёмной плитке и каким цветом её
 * подсветить.
 *
 * Картинок у передач в телепрограмме нет, поэтому плитку и верх панели
 * окрашивает сам логотип: мягкий отсвет его цвета. Оба ответа даёт одна
 * маленькая копия логотипа на холсте (логотипы приходят с нашего домена, холст
 * не «отравлен»). Ответ кэшируется по адресу и отдаётся синхронно: плитки
 * виртуальные и при прокрутке монтируются заново — отсвет не должен мигать.
 */
export interface LogoLook {
  /** Цвет отсвета тройкой «r, g, b» для `rgba(var(--tone), …)`; `null` — логотип бесцветный. */
  tone: string | null;
  /** Как рисовать сам логотип (см. `classifyLogo`). */
  look: LogoTone;
}

/** Ширина копии: тонкие буквы в меньшей смазались бы в прозрачность. */
const SAMPLE_W = 48;
/** Ниже такой средней насыщенности цвет логотипа — серый: отсвет не нужен. */
const NEUTRAL = 0.08;

const known = new Map<string, LogoLook>();
const pending = new Map<string, Promise<LogoLook>>();
const PLAIN: LogoLook = { tone: null, look: 'plain' };

function lookOf(url: string): Promise<LogoLook> {
  const ready = known.get(url);
  if (ready !== undefined) return Promise.resolve(ready);
  let promise = pending.get(url);
  if (promise === undefined) {
    promise = measure(url)
      .catch(() => PLAIN)
      .then((look) => {
        known.set(url, look);
        pending.delete(url);
        return look;
      });
    pending.set(url, promise);
  }
  return promise;
}

async function measure(url: string): Promise<LogoLook> {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  const ratio = image.naturalWidth > 0 && image.naturalHeight > 0 ? image.naturalHeight / image.naturalWidth : 0.5;
  const width = SAMPLE_W;
  const height = Math.max(4, Math.min(SAMPLE_W * 2, Math.round(SAMPLE_W * ratio)));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return PLAIN;
  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  return { tone: toneOf(pixels), look: classifyLogo(pixels) };
}

/**
 * Цвет отсвета: среднее непрозрачных пикселей, взвешенное по насыщенности, —
 * белые и чёрные буквы иначе утопили бы фирменный цвет в сером. Бесцветный
 * логотип (белый, чёрный, серебро) отсвета не даёт вовсе: подкрашенный в
 * случайный оттенок, он выглядел бы ошибкой.
 */
export function toneOf(pixels: ArrayLike<number>): string | null {
  let weight = 0;
  let red = 0;
  let green = 0;
  let blue = 0;
  let chroma = 0;
  let count = 0;
  for (let index = 0; index + 3 < pixels.length; index += 4) {
    const a = pixels[index + 3]! / 255;
    if (a < 0.5) continue;
    const r = pixels[index]! / 255;
    const g = pixels[index + 1]! / 255;
    const b = pixels[index + 2]! / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const w = (max - min) * (0.3 + max);
    red += r * w;
    green += g * w;
    blue += b * w;
    weight += w;
    chroma += max - min;
    count += 1;
  }
  if (count === 0 || weight === 0 || chroma / count < NEUTRAL) return null;
  return calmed(red / weight, green / weight, blue / weight);
}

/** Насыщенность и светлота — в коридоре благородного отсвета, без кислоты и грязи. */
function calmed(r: number, g: number, b: number): string {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let hue = 0;
  if (delta > 0) {
    if (max === r) hue = (g - b) / delta + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue /= 6;
  }
  const s = 0.62;
  const l = 0.48;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [channel(hue + 1 / 3), channel(hue), channel(hue - 1 / 3)].map((v) => Math.round(v * 255)).join(', ');
}

/** Вид логотипа по адресу: сразу из памяти, иначе после замера. */
export function useLogoLook(url: string | null): LogoLook | null {
  const [state, setState] = useState<{ url: string | null; look: LogoLook | null }>(() => ({
    url,
    look: url === null ? null : (known.get(url) ?? null),
  }));
  useEffect(() => {
    if (url === null || known.has(url)) return;
    let live = true;
    void lookOf(url).then((look) => {
      if (live) setState({ url, look });
    });
    return () => {
      live = false;
    };
  }, [url]);
  if (url === null) return null;
  if (state.url === url && state.look !== null) return state.look;
  return known.get(url) ?? null;
}
